<script setup lang="ts">
import { computed, ref } from "vue";
import { useFormKitContextById } from "@formkit/vue";
import { FilterPanel, FilterBox } from "../filters";
import { BulkSearch } from "@/components/common";
import StatusFilter from "./StatusFilter.vue";
import PriorityFilter from "./PriorityFilter.vue";
import { useBulkSearch } from "@/composables/BulkSearch";
import useTrackerStore from "@/stores/trackers";
import { RUDDERSTACK_EVENTS } from "@/lib/rudderstack";
import { tagOptions } from "@/utils/customerSupportTicketTags";
import { TicketDashboardRequestParams } from "@telegraphio/bapi-types";

const props = defineProps<{ defaultFilters: Partial<TicketDashboardRequestParams> }>();

const trackers = useTrackerStore();
const emit = defineEmits<{ apply: [value: Partial<TicketDashboardRequestParams>] }>();
const {
  updatePreviousSearchTermCount,
  resetBulkSearch,
  searchTerms,
  currentSearchTermsCount,
  previousSearchTermsCount,
} = useBulkSearch();

const draftFilters = ref<Partial<TicketDashboardRequestParams>>({ ...props.defaultFilters });

const formContext = useFormKitContextById<Partial<TicketDashboardRequestParams>>("customer-support-filters");
const formValue = computed(() => formContext.value?.value);

const filterChips = computed(() => {
  if (!formValue.value) return [];

  const priorities = Array.isArray(formValue.value.priority) ? formValue.value.priority : [];
  const stateArray = Array.isArray(formValue.value.state) ? formValue.value.state : [];
  const tagsArray = Array.isArray(formValue.value.tags) ? formValue.value.tags : [];
  const equipmentIds = Array.isArray(formValue.value.equipment_ids)
    ? formValue.value.equipment_ids
    : (formValue.value.equipment_ids?.split("\n") ?? []);

  return [
    {
      label: "Asset Search",
      value: `${equipmentIds.length}`,
      disabled: !equipmentIds.length,
    },
    {
      label: "Status",
      value: stateArray.length,
      disabled: !stateArray.length || stateArray.length === props.defaultFilters.state?.length,
    },
    {
      label: "Priority",
      value: priorities.length === 3 ? "All" : priorities.length,
      disabled: priorities.length === 3,
    },
    {
      label: "My Tickets",
      value: formValue.value.assignees?.length ? "Selected" : undefined,
      disabled: false,
    },
    {
      label: "Tags",
      value: tagsArray.length === 1 ? tagsArray[0] : tagsArray.length,
      disabled: false,
    },
    {
      label: "Keyword(s)",
      value: formValue.value.keyword_search ?? undefined,
      disabled: false,
    },
  ];
});

function resetFilters() {
  if (formContext.value) {
    formContext.value.node.reset();
    trackers.logRudderstackEvent(RUDDERSTACK_EVENTS.CUSTOMER_SUPPORT_TICKETS_RESET_FILTER, {});
    resetBulkSearch();

    emit("apply", { ...props.defaultFilters });
  }
}

function applyFilters() {
  if (!formValue.value) return undefined;
  trackers.logRudderstackEvent(RUDDERSTACK_EVENTS.CUSTOMER_SUPPORT_TICKETS_FILTER, {
    filters: formValue.value,
  });

  const priorityArray = Array.isArray(formValue.value.priority) ? formValue.value.priority : [];
  const stateArray = Array.isArray(formValue.value.state) ? formValue.value.state : [];

  // Only include assignees if it's a valid array with values
  const assignees = Array.isArray(formValue.value.assignees) ? formValue.value.assignees : undefined;

  draftFilters.value = {
    ...formValue.value,
    equipment_ids: searchTerms.value || undefined,
    priority: priorityArray,
    state: stateArray,
    assignees,
  };

  emit("apply", { ...draftFilters.value });
}

function searchAssets() {
  if (formValue.value) {
    updatePreviousSearchTermCount();
    draftFilters.value = {
      ...formValue.value,
      equipment_ids: searchTerms.value,
    };
  }
}

function resetAssetSearch() {
  resetBulkSearch();
  if (formValue.value) {
    draftFilters.value = {
      ...formValue.value,
      equipment_ids: undefined,
    };
  }
}
</script>

<template>
  <FilterPanel
    v-model="draftFilters"
    class="my-4"
    name="customer-support"
    :values="props.defaultFilters"
    :chips="filterChips"
    @apply="applyFilters"
    @reset="resetFilters"
  >
    <FilterBox class="col-span-full grid items-center lg:col-span-2 2xl:col-span-3">
      <div class="mb-3.5">
        <label for="cs-bulk-search" class="mb-0.5 text-sm font-bold">Bulk Search</label>
        <BulkSearch
          id="cs-bulk-search"
          v-model="searchTerms"
          name="equipment_ids"
          singular="Asset"
          plural="Assets"
          :disabled="false"
          :typed-entries-count="currentSearchTermsCount"
          :searched-for-entries-count="previousSearchTermsCount"
          placeholder="Enter one asset ID per line"
          @search="searchAssets"
          @clear="resetAssetSearch"
        />
      </div>
    </FilterBox>
    <FilterBox class="col-span-full grid items-center justify-self-stretch lg:col-span-2 2xl:col-span-3">
      <FormKit
        type="dropdown"
        data-testid="tags-input"
        name="tags"
        label="Tag Type"
        placeholder="- -"
        multiple
        :options="tagOptions"
      />
    </FilterBox>
    <FilterBox
      class="col-span-full grid auto-cols-max items-center gap-1.5 lg:grid-flow-col xl:col-start-5 xl:grid-flow-row 2xl:col-start-7 2xl:col-end-[-1] 2xl:grid-flow-col"
    >
      <FormKit type="text" name="keyword_search" label="Search" placeholder="Search by Keyword" prefix-icon="search" />
      <StatusFilter />
    </FilterBox>

    <FilterBox class="col-span-full pb-0">
      <PriorityFilter />
    </FilterBox>
  </FilterPanel>
</template>
